import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, map, Observable, of, switchMap, take } from 'rxjs';

import { ApolloClientService } from './apollo-client.service';
import { AuthService } from './auth.service';
import { UserQueries } from '../graphql/queries/user.queries';
import { GraphqlNexusUserOutput } from '../graphql/models/output/nexus-user-output.model';
import { FunctionalGroup } from '../models/functional-group';

const ADMIN_GROUP_NAME = 'Driving Restriction Admins';

@Injectable({ providedIn: 'root' })
export class UserService {

  private isAdminSubject = new BehaviorSubject<boolean>(localStorage.getItem('isAdmin') === 'true');
  public isAdmin$ = this.isAdminSubject.asObservable();

  constructor(
    private apolloClientService: ApolloClientService,
    private authService: AuthService,
  ) {
    this.authService.userData$.pipe(
      distinctUntilChanged((prev, curr) => prev.sessionID === curr.sessionID),
      filter(userData => !!userData.sessionID),
      switchMap(userData => this.resolveAdminStatus$(userData.userName).pipe(
        catchError(() => of(undefined))
      )),
    ).subscribe();
  }

  public resolveAdminStatus$(username: string): Observable<void> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      take(1),
      switchMap(client =>
        client.query<{ nexusUser: GraphqlNexusUserOutput }>({
          query: UserQueries.getNexusUser,
          variables: { username },
          fetchPolicy: 'network-only',
          context: { skipDefaultErrorHandling: true },
        })
      ),
      map(result => {
        const returnValue = result.data?.nexusUser?.returnValue;
        const groups: FunctionalGroup[] = returnValue
          ? (JSON.parse(returnValue).functionalGroups ?? [])
          : [];
        const isAdmin = groups.some(g => g.functionalGroupName === ADMIN_GROUP_NAME);
        localStorage.setItem('isAdmin', String(isAdmin));
        this.isAdminSubject.next(isAdmin);
      })
    );
  }
}
